/**
 * Data-source hooks for the Jira Integration settings page.
 *
 * Lists every Jira credential configured for the active org and
 * exposes mutations for create / rotate / rename / delete. The
 * backend routes live at `/api/v1/orgs/:orgId/jira-credentials`
 * (task 136 §D4); responses NEVER contain `botEmail` or the API
 * token.
 *
 * Task 137 §D7 added:
 *   - `useJiraCredentialDiscoveryRun(credentialId)` — polls the
 *     latest discovery run every 10 s while in flight, otherwise
 *     static. Drives the per-row status pill on the settings page.
 *   - `useTriggerJiraSyncNow()` — POST .../sync-now mutation.
 *
 * Mockup mode now ships a two-credential fixture (one succeeded,
 * one auth_failed) per task 137 §D7.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import {
  JIRA_CREDENTIALS_FIXTURE,
  JIRA_DISCOVERY_RUNS_FIXTURE,
  JIRA_WORKLOG_STATUS_FIXTURE,
} from '@/data/jiraCredentials';

export interface JiraCredential {
  id: string;
  siteUrl: string;
  label: string;
  lastRotatedAt: string;
  lastUsedAt: string | null;
  lastFailedAt: string | null;
  lastFailureReason: string | null;
  createdAt: string;
}

export interface CreateJiraCredentialBody {
  siteUrl: string;
  label: string;
  botEmail: string;
  apiToken: string;
}

export interface RotateJiraCredentialBody {
  credentialId: string;
  botEmail: string;
  apiToken: string;
}

export interface RenameJiraCredentialBody {
  credentialId: string;
  label: string;
}

export interface UseJiraCredentialsResult {
  credentials: readonly JiraCredential[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useJiraCredentials(): UseJiraCredentialsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['jiraCredentials', orgId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<JiraCredential[]>(`/api/v1/orgs/${orgId}/jira-credentials`, token);
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    return {
      credentials: JIRA_CREDENTIALS_FIXTURE,
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  return {
    credentials: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error ?? null,
  };
}

// ── Discovery-run polling hook (task 137 §D7) ───────────────────────

export interface JiraDiscoveryRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'succeeded' | 'auth_failed' | 'rate_limited' | 'upstream_error' | 'abandoned';
  trigger: 'credential_created' | 'credential_rotated' | 'cron' | 'manual';
  projectsSeen: number;
  projectsArchived: number;
  usersSeen: number;
  usersDeactivated: number;
  linksCreated: number;
  errorDetail: string | null;
}

const POLL_INTERVAL_MS = 10_000;

/**
 * Returns the latest discovery run for a credential, polling every
 * 10 s while in flight (status='running'), otherwise static. Drives
 * the per-row status pill on the settings page.
 */
export function useJiraCredentialDiscoveryRun(credentialId: string): {
  lastRun: JiraDiscoveryRun | null;
  recentRuns: readonly JiraDiscoveryRun[];
  isLoading: boolean;
} {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['jiraDiscoveryRuns', orgId, credentialId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<JiraDiscoveryRun[]>(
        `/api/v1/orgs/${orgId}/jira-credentials/${credentialId}/discovery-runs?limit=10`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!credentialId,
    refetchInterval: (q) => {
      const data = q.state.data as JiraDiscoveryRun[] | undefined;
      const latest = data?.[0];
      return latest && latest.status === 'running' ? POLL_INTERVAL_MS : false;
    },
  });

  if (mockupMode) {
    const runs = JIRA_DISCOVERY_RUNS_FIXTURE[credentialId] ?? [];
    return {
      lastRun: runs[0] ?? null,
      recentRuns: runs,
      isLoading: false,
    };
  }

  const data = query.data ?? [];
  return {
    lastRun: data[0] ?? null,
    recentRuns: data,
    isLoading: query.isLoading,
  };
}

// ── Worklog-status hook (task 138 §D7) ──────────────────────────────

export interface JiraWorklogStatus {
  /** Most recent last_refreshed_at across this credential's worklog rows; null if none yet. */
  lastRefreshedAt: string | null;
  totalWorklogs: number;
  orphans: number;
  /** Cursor positions — strings because BigInt doesn't round-trip JSON. */
  worklogUpdatedCursorMs: string | null;
  worklogDeletedCursorMs: string | null;
}

/**
 * Returns worklog-poller freshness + counts for a credential. Drives
 * the "Worklogs: Last synced N m ago" line in the per-row detail
 * panel on the settings page. Refreshed every 30 s while the user
 * is on the page.
 *
 * Mockup mode returns a deterministic fixture so the expanded row
 * shows realistic numbers without hitting the backend.
 */
export function useJiraCredentialWorklogStatus(credentialId: string): {
  status: JiraWorklogStatus | null;
  isLoading: boolean;
} {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['jiraWorklogStatus', orgId, credentialId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<JiraWorklogStatus>(
        `/api/v1/orgs/${orgId}/jira-credentials/${credentialId}/worklog-status`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!credentialId,
    refetchInterval: 30_000,
  });

  if (mockupMode) {
    // Lookup in the src/data/ fixture. Dates live there (CLAUDE.md
    // convention — bare `new Date()` outside src/data/ and
    // src/lib/frozenTime.ts is an ESLint error).
    return {
      status: JIRA_WORKLOG_STATUS_FIXTURE[credentialId] ?? null,
      isLoading: false,
    };
  }

  return {
    status: query.data ?? null,
    isLoading: query.isLoading,
  };
}

export function useTriggerJiraSyncNow() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (credentialId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<{ jobId: string }>(
        `/api/v1/orgs/${currentOrg.id}/jira-credentials/${credentialId}/sync-now`,
        token,
        { method: 'POST' },
      );
    },
    onSuccess: (_data, credentialId) => {
      void qc.invalidateQueries({ queryKey: ['jiraCredentials', currentOrg.id] });
      void qc.invalidateQueries({
        queryKey: ['jiraDiscoveryRuns', currentOrg.id, credentialId],
      });
      void qc.invalidateQueries({
        queryKey: ['jiraWorklogStatus', currentOrg.id, credentialId],
      });
    },
  });
}

function useInvalidator() {
  const { currentOrg } = useAppContext();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['jiraCredentials', currentOrg.id] });
  };
}

export function useCreateJiraCredential() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (body: CreateJiraCredentialBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<JiraCredential>(`/api/v1/orgs/${currentOrg.id}/jira-credentials`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: invalidate,
  });
}

export function useRotateJiraCredential() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async ({ credentialId, botEmail, apiToken }: RotateJiraCredentialBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<JiraCredential>(
        `/api/v1/orgs/${currentOrg.id}/jira-credentials/${credentialId}/rotate`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({ botEmail, apiToken }),
        },
      );
    },
    onSuccess: invalidate,
  });
}

export function useRenameJiraCredential() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async ({ credentialId, label }: RenameJiraCredentialBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<JiraCredential>(
        `/api/v1/orgs/${currentOrg.id}/jira-credentials/${credentialId}`,
        token,
        {
          method: 'PATCH',
          body: JSON.stringify({ label }),
        },
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteJiraCredential() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (credentialId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/jira-credentials/${credentialId}`,
        token,
        { method: 'DELETE' },
      );
    },
    onSuccess: invalidate,
  });
}
